import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Card } from '@/components/Card';
import { ErrorBanner } from '@/components/ErrorBanner';
import { PageHeader } from '@/components/PageHeader';
import { Spinner } from '@/components/Spinner';
import { RangeTabs, type RangeDays } from '@/components/progress/RangeTabs';
import { TrendChart, type TrendPoint } from '@/components/progress/TrendChart';
import { formatDate, formatKg, formatNumber, formatVolume } from '@/components/workout/format';
import { equipmentLabel, muscleLabel } from '@/lib/i18n/training';
import { lastDays, useExerciseProgress } from '@/lib/queries/progress';

export const Route = createFileRoute('/_app/progress/exercises/$exerciseId')({
  component: ExerciseProgressPage,
});

function Best({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-surface px-2 py-2 xs:px-3">
      {/* Sin partir «85,4 kg» en dos líneas: en un móvil estrecho baja un
          punto de tamaño en vez de dejar la unidad sola debajo. */}
      <p className="text-base font-bold whitespace-nowrap text-ink tabular-nums xs:text-lg">
        {value}
      </p>
      <p className="text-xs text-ink-muted">{label}</p>
    </div>
  );
}

function ExerciseProgressPage() {
  const { exerciseId } = Route.useParams();
  const [days, setDays] = useState<RangeDays>(90);
  const range = useMemo(() => lastDays(days), [days]);
  const progress = useExerciseProgress(exerciseId, range, 60);

  // El servidor devuelve el historial de la sesión más reciente a la más
  // antigua; una gráfica temporal se lee al revés.
  const history = useMemo(() => [...(progress.data?.history ?? [])].reverse(), [progress.data]);
  const oneRmPoints = useMemo<TrendPoint[]>(
    () => history.map((point) => ({ label: formatDate(point.date), value: point.estimated1RmKg })),
    [history],
  );
  const volumePoints = useMemo<TrendPoint[]>(
    () => history.map((point) => ({ label: formatDate(point.date), value: point.volumeKg })),
    [history],
  );

  if (progress.isPending) {
    return (
      <div className="flex justify-center py-10">
        <Spinner size="lg" />
      </div>
    );
  }

  if (progress.isError) {
    return (
      <>
        <PageHeader title="Progreso del ejercicio" />
        <ErrorBanner
          title="No hemos podido cargar el ejercicio"
          error={progress.error}
          onRetry={() => void progress.refetch()}
        />
      </>
    );
  }

  const { exercise } = progress.data;
  const media = [...exercise.media].sort((a, b) => a.position - b.position)[0];

  return (
    <>
      <PageHeader
        title={exercise.nameEs}
        subtitle={`${muscleLabel(exercise.primaryMuscle)} · ${equipmentLabel(exercise.equipment)}`}
        action={
          <Link
            to="/progress"
            aria-label="Volver a Progreso"
            className="flex size-11 items-center justify-center rounded-xl border border-line text-ink-muted transition-colors hover:bg-surface-raised active:bg-surface-raised"
          >
            <ArrowLeft className="size-5" aria-hidden="true" />
          </Link>
        }
      />

      <div className="flex flex-col gap-4">
        {media === undefined ? null : (
          <img
            src={media.url}
            alt={exercise.nameEs}
            loading="lazy"
            className="max-h-56 w-full rounded-2xl border border-line object-cover"
          />
        )}

        <RangeTabs value={days} onChange={setDays} />

        <Card title="Tus mejores marcas">
          <div className="grid grid-cols-3 gap-2 text-center">
            <Best
              label="1RM estimado"
              value={
                progress.data.best1RmKg === null
                  ? '—'
                  : `${formatNumber(progress.data.best1RmKg)} kg`
              }
            />
            <Best label="Peso máximo" value={formatKg(progress.data.bestWeightKg)} />
            <Best label="Mejor sesión" value={formatVolume(progress.data.bestVolumeSessionKg)} />
          </div>
        </Card>

        <Card title="1RM estimado" description="La mejor serie de cada sesión, con Epley.">
          <TrendChart
            data={oneRmPoints}
            name="1RM estimado"
            format={(value) => `${formatNumber(value)} kg`}
            emptyMessage="Necesitas al menos dos sesiones con este ejercicio."
          />
        </Card>

        <Card title="Volumen por sesión" description="Peso × repeticiones de todas sus series.">
          <TrendChart
            data={volumePoints}
            name="Volumen"
            format={(value) => formatVolume(value)}
            emptyMessage="Necesitas al menos dos sesiones con este ejercicio."
          />
        </Card>

        <Card title="Historial">
          {history.length === 0 ? (
            <p className="text-sm text-ink-muted">
              No has registrado este ejercicio en el periodo elegido.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-ink-faint">
                    <th scope="col" className="py-1 pr-2 font-medium">
                      Fecha
                    </th>
                    <th scope="col" className="py-1 pr-2 font-medium">
                      Serie top
                    </th>
                    <th scope="col" className="py-1 pr-2 font-medium">
                      1RM est.
                    </th>
                    <th scope="col" className="py-1 text-right font-medium">
                      Volumen
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[...history].reverse().map((point) => (
                    <tr key={point.sessionId} className="border-t border-line/60">
                      <td className="py-1.5 pr-2 text-ink-muted">{formatDate(point.date)}</td>
                      <td className="py-1.5 pr-2 text-ink tabular-nums">
                        {formatNumber(point.topSetWeightKg)} kg × {point.topSetReps}
                      </td>
                      <td className="py-1.5 pr-2 text-ink tabular-nums">
                        {formatNumber(point.estimated1RmKg)} kg
                      </td>
                      <td className="py-1.5 text-right text-ink-muted tabular-nums">
                        {formatVolume(point.volumeKg)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
